import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiClient } from '../../api/client';
import WebApp from '@twa-dev/sdk';
import posthog from 'posthog-js';

interface PaymentRequiredProps {
  creationId: string;
  processingPrice: number;
  currentBalance: number;
  onPaymentComplete: (updatedBalance: number) => void;
  pricingInfo?: string;
  strategyType?: string;
}

export function PaymentRequired({ creationId, processingPrice, currentBalance, onPaymentComplete, pricingInfo, strategyType }: PaymentRequiredProps) {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation();

  const handleAddBalance = () => {
    navigate('/balance');
  };

  const handlePay = async () => {
    setIsProcessing(true);
    posthog.capture('purchase_clicked', { creation_id: creationId, amount: processingPrice });
    try {
      await ApiClient.startProcessing(creationId);
      
      // PostHog: Capture processing started event
      posthog.capture('creation_started', { 
        creation_id: creationId, 
        strategy_type: strategyType
      });
      
      const userInfo = await ApiClient.getUserInfo();
      onPaymentComplete(userInfo.balance);
    } catch (error) {
      console.error('Failed to process payment:', error);
      // Show error to user
      const tg = WebApp;
      await tg.showPopup({
        title: t('errors.errorTitle'),
        message: t('paymentRequired.paymentFailedError'),
        buttons: [{ type: 'ok' }]
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const hasEnoughBalance = currentBalance >= processingPrice;

  return (
    <div className="bg-surface-interactive dark:bg-surface-interactive-dark border border-interactive dark:border-interactive-dark rounded-2xl p-6 shadow-interactive">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-theme-text">{t('paymentRequired.title')}</h3>
        <div className="flex items-center gap-2 text-theme-hint">
          <span className="text-xl">✨</span>
          <span className="font-medium">{processingPrice}</span>
          {pricingInfo && (
            <span className="text-xs text-theme-hint">
              ({pricingInfo})
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between p-4 bg-neutral-soft dark:bg-neutral-soft-dark rounded-xl mb-6">
        <div className="text-theme-text">{t('paymentRequired.yourBalance')}</div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-lg">✨</span>
            <span className="font-medium">{currentBalance}</span>
          </div>
          {!hasEnoughBalance && (
            <button
              onClick={handleAddBalance}
              className="px-3 py-1.5 text-sm bg-primary-accent text-on-primary-accent rounded-lg hover:bg-primary-accent-hover transition-colors"
            >
              {t('paymentRequired.addMoreButton')}
            </button>
          )}
        </div>
      </div>

      <button
        onClick={handlePay}
        disabled={!hasEnoughBalance || isProcessing}
        className="w-full py-3 px-4 bg-primary-accent text-on-primary-accent font-medium rounded-xl hover:bg-primary-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {isProcessing ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-on-primary-accent border-t-transparent rounded-full animate-spin" />
            <span>{t('paymentRequired.processingButton')}</span>
          </div>
        ) : hasEnoughBalance ? (
          t('paymentRequired.payButton')
        ) : (
          t('paymentRequired.insufficientBalanceButton')
        )}
      </button>
    </div>
  );
} 